/**
 * RecentTasksList — the "Recent tasks" Inbox section (founder mandate 2026-07-05).
 *
 * A flat, NEWEST-first list of the cards recently touched (created / edited / a
 * process finished on them). Sits at the BOTTOM of the Inbox (below Needs You /
 * Notifications). NO hard limit — the list is keyset-paginated and LAZILY loads
 * the next (older) page only when the bottom sentinel scrolls into view, so the
 * page never fetches everything at once.
 *
 * CROSS-ORG (founder 2026-07-05): uses the GLOBAL endpoint so it spans ALL the
 * user's orgs, not just the active one. Each row shows an ORG badge (which org
 * the card belongs to) next to the Project · Board breadcrumb, and clicking a
 * card in a NON-active org switches org context first so it opens correctly.
 *
 * Each row is a POINTER to the card: title + "Project · Board" breadcrumb (small,
 * muted) + relative activity time ("2h ago"; absolute in the title tooltip) + a
 * monochrome status dot (the card's column kind). Click → deep-link into the
 * card's board/card route WITH ?tab=session so the user lands directly in the
 * card's session view.
 *
 * States: loading (skeleton), empty ("No recent tasks"), error (with retry),
 * "loading more" spinner at the sentinel, and an end-of-list terminus.
 *
 * ADR-057: monochrome, semantic tokens only. English only. Mobile-friendly:
 * full-width rows, ≥56px tap targets, thumb-zone reachable.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import * as React from 'react';

import { env } from '../../env';
import {
  recentCardsApi,
  recentCardsKeys,
  type RecentCard,
} from '../../lib/api/inbox-recent';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { OrgBadge } from './org-badge';
import { useInboxNavigate, useOrgLabel } from './inbox-shared';

const PAGE_SIZE = 30;

interface RecentTasksListProps {
  /** Called after a row click navigates (close a popover/sheet). */
  onNavigate?: () => void;
  /**
   * Scroll container the lazy-load sentinel observes against. On the full page
   * the list lives in the document/viewport scroll, so leave undefined (null →
   * viewport root). Inside the bell popover/sheet it lives in a BOUNDED inner
   * scroller, so pass that element or the sentinel never intersects and the next
   * (older) page never loads.
   */
  scrollRoot?: HTMLElement | null;
}

export function RecentTasksList({
  onNavigate,
  scrollRoot,
}: RecentTasksListProps): React.ReactElement {
  const me = useAuthStore((s) => s.user);
  // Global Inbox: only needs an authenticated user (spans all the user's orgs).
  const enabled = !!me;

  const query = useInfiniteQuery({
    queryKey: recentCardsKeys.global(),
    queryFn: ({ pageParam }) => recentCardsApi.listGlobal(pageParam, PAGE_SIZE),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    refetchOnWindowFocus: false,
  });

  const items: RecentCard[] = React.useMemo(
    () => (query.data ? query.data.pages.flatMap((p) => p.items) : []),
    [query.data],
  );

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = query;

  // ── Lazy load: fire fetchNextPage when the sentinel enters the viewport ──
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      // root: the bounded popover/sheet scroller when provided (else the viewport
      // on the full page). rootMargin pre-fetches a little before it's visible.
      { root: scrollRoot ?? null, rootMargin: '200px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length, scrollRoot]);

  return (
    <section
      className={cn('flex shrink-0 flex-col', !env.VITE_DESIGN_LIGHT && 'border-t border-border')}
      data-testid="recent-tasks"
    >
      <header className="flex items-center gap-2 px-4 py-2 md:px-6">
        <h2
          className={
            env.VITE_DESIGN_LIGHT
              ? 'text-[12px] font-medium text-muted-foreground'
              : 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
          }
        >
          Recent tasks
        </h2>
      </header>

      {isLoading ? (
        <RecentTasksSkeleton />
      ) : isError ? (
        <div className="flex flex-col items-start gap-2 px-4 py-6 md:px-6">
          <span className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
            <AlertCircle className="h-4 w-4" aria-hidden /> Couldn’t load recent tasks.
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded border border-border px-2 py-1 text-[12px] text-foreground transition-colors hover:bg-foreground/5"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-muted-foreground md:px-6">No recent tasks.</p>
      ) : (
        <ul
          className={cn(!env.VITE_DESIGN_LIGHT && 'divide-y divide-border')}
          data-testid="recent-tasks-list"
        >
          {items.map((card) => (
            <RecentTaskRow key={card.cardId} card={card} onNavigate={onNavigate} />
          ))}
        </ul>
      )}

      {/* Sentinel + terminus. Rendered only once there's a real list. */}
      {!isLoading && !isError && items.length > 0 && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center px-4 py-3 text-[12px] text-muted-foreground md:px-6"
          data-testid="recent-tasks-sentinel"
        >
          {isFetchingNextPage ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading more…
            </span>
          ) : hasNextPage ? (
            <span className="h-4" aria-hidden />
          ) : (
            <span className="text-[11px] text-muted-foreground/70">End of list</span>
          )}
        </div>
      )}
    </section>
  );
}

// ── Row ───────────────────────────────────────────────────────────────
interface RecentTaskRowProps {
  card: RecentCard;
  onNavigate?: () => void;
}

function RecentTaskRow({ card, onNavigate }: RecentTaskRowProps): React.ReactElement {
  const inboxNavigate = useInboxNavigate();
  // I2 (light): the org name leads the crumb as plain muted text instead of a
  // bordered OrgBadge chip.
  const orgName = useOrgLabel(card.orgId);

  const handleClick = async (): Promise<void> => {
    onNavigate?.();
    if (!card.boardId) return;
    // Deep-link into the card, forced into SESSION mode (?tab=session). The card
    // may live in a NON-active org, so switch org context first (the nav helper
    // does that) or the board would mount against the wrong org.
    await inboxNavigate(
      {
        to: '/board/$boardId/card/$cardId',
        params: { boardId: card.boardId, cardId: card.cardId },
        search: { tab: 'session' },
      } as never,
      card.orgId,
    );
  };

  const crumbs = (
    env.VITE_DESIGN_LIGHT
      ? [orgName, card.projectName, card.boardName]
      : [card.projectName, card.boardName]
  )
    .filter(Boolean)
    .join(' · ');
  const statusLabel = statusDisplay(card.status);

  return (
    <li>
      <button
        type="button"
        onClick={() => void handleClick()}
        data-testid="recent-task-row"
        disabled={!card.boardId}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors md:px-6',
          'min-h-[56px]',
          card.boardId
            ? 'hover:bg-foreground/5 active:bg-foreground/10'
            : 'cursor-default opacity-60',
        )}
      >
        {/* Monochrome status dot — the card's column kind. Colour intentionally
            neutral per ADR-057; the label lives in the tooltip / a11y name. */}
        <span
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-foreground/40"
          title={statusLabel}
          aria-label={statusLabel}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            {/* Org — a bordered chip today; a plain first crumb under the light
                system (I2, folded into `crumbs`). */}
            {env.VITE_DESIGN_LIGHT ? null : <OrgBadge orgId={card.orgId} />}
            {crumbs ? (
              <span className="truncate text-[11px] text-muted-foreground">{crumbs}</span>
            ) : (
              <span className="text-[11px] text-muted-foreground">{statusLabel}</span>
            )}
            <time
              dateTime={card.recencyAt}
              title={absoluteTime(card.recencyAt)}
              className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground"
            >
              {relativeTime(card.recencyAt)}
            </time>
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium text-foreground">
            {card.title ?? 'Untitled card'}
          </span>
        </span>
        {/* I3 (light): drop the per-row trailing arrow — the row is the affordance. */}
        {!env.VITE_DESIGN_LIGHT && card.boardId ? (
          <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
      </button>
    </li>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────
function RecentTasksSkeleton(): React.ReactElement {
  return (
    <ul
      className={cn(!env.VITE_DESIGN_LIGHT && 'divide-y divide-border')}
      aria-hidden
      data-testid="recent-tasks-skeleton"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3 md:px-6" style={{ minHeight: 56 }}>
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-foreground/10" />
          <span className="min-w-0 flex-1">
            <span className="block h-2.5 w-1/3 rounded bg-foreground/10" />
            <span className="mt-2 block h-3 w-3/5 rounded bg-foreground/10" />
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Presentation helpers ──────────────────────────────────────────────

/**
 * Human status label from a column kind. Keeps copy in one place; the dot stays
 * monochrome (ADR-057) so this only feeds the tooltip / screen-reader name.
 */
function statusDisplay(kind: string | null): string {
  switch (kind) {
    case 'backlog':
      return 'Backlog';
    case 'todo':
      return 'To do';
    case 'formulating':
      return 'Formulating';
    case 'in_progress':
      return 'In progress';
    case 'for_approval':
      return 'For approval';
    case 'done':
      return 'Done';
    case 'archive':
      return 'Archived';
    default:
      return 'Card';
  }
}

/**
 * Relative "time ago" for the activity stamp. Unlike lib/kanban card-status's
 * `formatAgo` (intentionally 24h-bounded for the run indicator), Recent tasks
 * has no time cap, so this extends the same "Xm/Xh ago" idiom to days / weeks /
 * months / years. Resolved on the client at render (no server tz baked in).
 */
function relativeTime(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const ms = now.getTime() - t;
  if (ms < 0) return 'just now'; // clock-skew future stamp
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (days < 30) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (days < 365) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/** Absolute local datetime for the tooltip, e.g. "Jul 5, 2026, 9:42 AM". */
function absoluteTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}
